// src/pages/Upload/CsvUploadPage.jsx
import React, { useRef } from "react";
import { useParams } from "react-router-dom";
import Card from "../../../components/ui/Card.jsx";
import UploadTabs from "./UploadTabs.jsx";
import { CODE_LABELS, codeToLabel } from "./constants.js";
import ConfirmationModal from "../../../components/ui/ConfirmationModal.jsx";
import {
  FileSpreadsheet,
  Loader2,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { validateCSVFile } from "../../../utils/csvValidator.js";
import toast from "react-hot-toast";
import { getUserFriendlyError } from "../../../utils/errorMessages.js";

// ⭐ High-level helper: upload_file + import_mortgage_products
import {
  uploadAndImportInvoiceFinanceProducts,
} from "../../../api/lenderPortal/csvUpload.js";

const isInvoiceFinance = (ptype) => ptype === "if";

/** Build lender-friendly lines from Invoice Finance import row issues. */
function formatIfRowIssues(rows = []) {
  const lines = [];
  for (const row of rows) {
    const prefix = `Row ${row.row}${row.lender ? ` (${row.lender})` : ""}`;
    const issues = Array.isArray(row?.issues) ? row.issues : [];
    for (const issue of issues) {
      const label = issue?.label || issue?.field || "Field";
      if (issue.severity === "error" || row.status === "failed") {
        lines.push(`${prefix}: ${label} — ${issue.message || "Invalid value"}`);
      }
    }
    if (row.status === "failed" && issues.length === 0) {
      lines.push(`${prefix}: Import failed for this row.`);
    }
  }
  return lines;
}

/**
 * Convert technical backend error text into wording that's easy for lenders to understand.
 */
function formatBackendErrorForLender(rawError) {
  if (!rawError || typeof rawError !== "string") return rawError;
  let msg = rawError.trim();

  // Remove "[Mortgage Product, BTL-xxxxx]:" prefix – not helpful for lenders
  msg = msg.replace(/^\[[^\]]+\]:\s*/i, "").trim();
  // Remove "Error: Value missing for Mortgage Product: " prefix from server messages
  msg = msg.replace(/^Error:\s*Value missing for [^:]+:\s*/i, "").trim();

  // Map technical field names to clear labels
  const fieldLabels = {
    product_expiray_date: "Product Expiry Date",
    product_expiry_date: "Product Expiry Date",
    expiry_date: "Expiry Date",
    leasehold_remaining_termyears: "Leasehold remaining term (years)",
    product_name: "Product Name",
    product_type: "Product Type",
    lender_product_code: "Lender Product Code",
    loan_purpose: "Loan Purpose",
    mortgage_term: "Mortgage Term",
    ltv: "LTV (%)",
    min_loan: "Min Loan",
    max_loan: "Max Loan",
    product_rate: "Product Rate",
    aprc: "APRC",
    valuation_fee: "Valuation Fee",
    legal_fee: "Legal Fee",
    arrangement_fee: "Arrangement Fee",
    borrower_type: "Borrower Type",
    epc_rating_green: "EPC rating (green product)",
  };

  const lowerMsg = msg.toLowerCase();
  const lowerMsgNormalized = lowerMsg.replace(/\s+/g, "_");
  for (const [tech, label] of Object.entries(fieldLabels)) {
    const techLower = tech.toLowerCase();
    const matches = lowerMsg.includes(techLower) || lowerMsgNormalized.includes(techLower) || lowerMsg.includes(techLower.replace(/_/g, " "));
    if (matches) {
      // Rewrite common patterns into plain English
      if (lowerMsg.includes("missing") || lowerMsg.includes("required") || msg === tech || lowerMsg === techLower || lowerMsg.replace(/\s/g, "_") === techLower) {
        return `${label} is missing or invalid.`;
      }
      if (lowerMsg.includes("value missing")) {
        return `${label} is missing. Please add a value in your CSV.`;
      }
      // Default: mention the field in a sentence
      return `${label}: ${msg}`;
    }
  }

  // If it looks like a field name only (snake_case), turn it into a readable label
  if (/^[a-z][a-z0-9_]*$/.test(msg)) {
    const readable = msg
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return `${readable} is missing or invalid.`;
  }

  return msg;
}

function btlSplitSummary(stats) {
  if (!stats || stats.complexBtlRows == null) return "";
  const breakdown = (stats.complexBreakdown || [])
    .filter((g) => g.rows > 0)
    .map((g) => `${g.label} ${g.rows}`)
    .join(", ");
  return `${stats.btlRows} BTL · ${stats.complexBtlRows} Complex BTL${breakdown ? ` (${breakdown})` : ""}`;
}

export default function CsvUploadPage() {
  const { ptype } = useParams();
  const isIF = isInvoiceFinance(ptype);
  const productLabel =
    (typeof codeToLabel === "function" ? codeToLabel(ptype) : null) ||
    CODE_LABELS[ptype] ||
    "Select Product";

  const [file, setFile] = React.useState(null);
  const [scheduleEnabled, setScheduleEnabled] = React.useState(false);
  const [scheduleAt, setScheduleAt] = React.useState("");
  const [errors, setErrors] = React.useState({});
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadStatus, setUploadStatus] = React.useState(null);
  const [validationResult, setValidationResult] = React.useState(null);
  const [isValidating, setIsValidating] = React.useState(false);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [uploadResult, setUploadResult] = React.useState(null);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadedFileUrl, setUploadedFileUrl] = React.useState(null);
  const [isUploadingFile, setIsUploadingFile] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = useRef(null);

  const handleFile = React.useCallback(async (picked) => {
    if (!picked) return;
    setFile(picked);
    setUploadStatus(null);
    setValidationResult(null);
    setUploadedFileUrl(null);
    setUploadProgress(0);
    setErrors((prev) => ({ ...prev, file: undefined, upload: undefined }));
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (isIF) {
      setIsUploadingFile(false);
      setUploadProgress(0);
      return;
    }
    setIsUploadingFile(true);
    setUploadProgress(0);
    try {
      const { uploadCsvFile } = await import("../../../api/lenderPortal/csvUpload");
      const uploadResult = await uploadCsvFile(picked, (progress) => setUploadProgress(progress));
      if (uploadResult?.file_url) setUploadedFileUrl(uploadResult.file_url);
      else throw new Error("Upload succeeded but no file URL returned.");
    } catch (error) {
      console.error("❌ Failed to upload CSV file:", error);
      setErrors((prev) => ({ ...prev, file: error?.message || "Failed to upload file. Please try again." }));
      setFile(null);
    } finally {
      setIsUploadingFile(false);
      setUploadProgress(0);
    }
  }, [isIF]);

  const onPick = async (e) => {
    const picked = e.target.files?.[0] || null;
    e.target.value = "";
    await handleFile(picked);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploadingFile && !isUploading) setIsDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isUploadingFile || isUploading) return;
    const dropped = e.dataTransfer?.files?.[0];
    if (!dropped) return;
    const name = (dropped.name || "").toLowerCase();
    const valid = name.endsWith(".csv") || name.endsWith(".xls") || name.endsWith(".xlsx");
    if (!valid) {
      setErrors((prev) => ({ ...prev, file: "Please drop a CSV, XLS, or XLSX file." }));
      return;
    }
    handleFile(dropped);
  };

  const downloadTemplate = async () => {
    if (isIF) {
      toast("Use the Invoice Finance XLSX template provided by Dynesourcer.", { icon: "ℹ️" });
      return;
    }
    try {
      // Fetch the template file from public folder
      const response = await fetch("/Dynesourcer_CSV_FINAL.csv");
      if (!response.ok) {
        throw new Error("Template file not found");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Dynesourcer_CSV_FINAL.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded successfully");
    } catch (error) {
      console.error("Failed to download template:", error);
      toast.error("Failed to download template file");
    }
  };

  const validateBasic = () => {
    const e = {};

    if (!file) e.file = "Please select a CSV file.";
    if (scheduleEnabled && !scheduleAt) {
      e.scheduleAt = "Choose a schedule date & time.";
    }

    if (file) {
      const validTypes = isIF ? [".csv", ".xls", ".xlsx"] : [".csv", ".xlsx"];
      const fileExtension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();

      if (!validTypes.includes(fileExtension)) {
        e.file = isIF ? "Please select a valid CSV, XLS, or XLSX file." : "Please select a valid CSV or XLSX file.";
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        e.file = "File size must be less than 10MB.";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateCSV = async () => {
    if (!file) {
      setErrors({ file: "Please select a CSV file." });
      toast.error("Please select a file first");
      return;
    }

    setIsValidating(true);
    setValidationResult(null);
    setErrors({});

    try {
      const result = await validateCSVFile(file);
      setValidationResult(result);

      if (result.valid) {
        toast.success(
          `Validation passed! ${result.stats.totalRows} row(s) ready to upload — ${btlSplitSummary(result.stats)}`
        );
      } else {
        toast.error(`Validation failed: ${result.errors.length} error(s) found`);
      }
    } catch (error) {
      console.error("Validation error:", error);
      const friendlyError = getUserFriendlyError(error);
      toast.error(friendlyError);
      setValidationResult({
        valid: false,
        errors: [friendlyError],
        warnings: [],
      });
    } finally {
      setIsValidating(false);
    }
  };

  const processUpload = async () => {
    if (!validateBasic()) return;

    if (!isIF && !uploadedFileUrl) {
      toast.error("Please wait for the file to finish uploading");
      return;
    }

    if (!isIF) {
      if (!validationResult) {
        toast.error("Please validate the file first before uploading");
        return;
      }

      if (!validationResult.valid) {
        toast.error("Please fix validation errors before uploading");
        return;
      }
    }

    setIsUploading(true);
    setUploadStatus(null);
    setErrors({});

    try {
      if (isIF) {
        const imported = await uploadAndImportInvoiceFinanceProducts(file, (progress) => {
          setUploadProgress(progress);
        });

        const uploadedFileName =
          imported?.filename || file?.name || "your file";
        const totalRows = imported?.total_rows ?? 0;
        const created = imported?.created ?? 0;
        const updated = imported?.updated ?? 0;
        const skipped = imported?.skipped ?? 0;
        const failed = imported?.failed ?? 0;
        const rows = Array.isArray(imported?.rows) ? imported.rows : [];
        const apiErrors = Array.isArray(imported?.errors) ? imported.errors : [];
        const rowErrorLines = formatIfRowIssues(rows.filter((r) => r?.status === "failed"));
        const apiErrorLines = apiErrors.map((e) =>
          typeof e === "string" ? e : e?.message || JSON.stringify(e)
        );
        const backendErrors = [...apiErrorLines, ...rowErrorLines];
        const hasFailures =
          imported?.status !== "success" || failed > 0 || backendErrors.length > 0;

        if (hasFailures) {
          const rowWord = failed === 1 ? "row" : "rows";
          setUploadResult({
            type: "error",
            message:
              failed > 0
                ? `We couldn't import all products — ${failed} ${rowWord} failed. Please review the details below and try again.`
                : "Import did not complete successfully. Please review the details below and try again.",
            fileName: uploadedFileName,
            backendErrors: backendErrors.length > 0 ? backendErrors : undefined,
          });
          setUploadStatus("error");
          setShowConfirmation(true);
          setErrors({ upload: "Please fix the issues listed in the message and upload again." });
        } else {
          const warningCount = rows.reduce((n, r) => {
            const issues = Array.isArray(r?.issues) ? r.issues : [];
            return n + issues.filter((i) => i?.severity === "warning").length;
          }, 0);

          setUploadResult({
            fileName: uploadedFileName,
            isScheduled: false,
            totalRows,
            importInfo: imported,
            ifSummary: { created, updated, skipped, failed, warningCount },
          });
          setShowConfirmation(true);
          setUploadStatus("success");
        }
        return;
      }

      // BTL — upload_file + import_mortgage_products
      let fileUrl = uploadedFileUrl;
      
      // If no pre-uploaded URL, upload now (shouldn't happen, but fallback)
      if (!fileUrl && file) {
        const { uploadCsvFile } = await import("../../../api/lenderPortal/csvUpload");
        const uploadResult = await uploadCsvFile(file, (progress) => {
          setUploadProgress(progress);
        });
        fileUrl = uploadResult.file_url;
      }
      
      if (!fileUrl) {
        throw new Error("No file URL available. Please select a file again.");
      }
      
      // Import using the uploaded file URL
      const { importMortgageProducts } = await import("../../../api/lenderPortal/csvUpload");
      const imported = await importMortgageProducts({
        file_url: fileUrl,
        product_type: ptype || "",
        product_categories: validationResult?.productCategories,
        schedule_enabled: scheduleEnabled,
        schedule_at: scheduleAt,
      });
      
      const upload = { file_url: fileUrl, file_name: file?.name || "upload.csv", name: file?.name || "upload.csv" };

      console.log("📁 CSV upload info:", upload);
      console.log("📊 Mortgage import info:", imported);

      const uploadedFileName =
        upload?.file_name || upload?.name || file.name || "your file";

      // Check for backend validation/processing errors (e.g. product_expiray_date missing)
      const backendResults = Array.isArray(imported?.results) ? imported.results : [];
      const backendErrors = backendResults.filter((r) => r && r.error);
      const hasBackendErrors = backendErrors.length > 0;

      if (hasBackendErrors) {
        // Parse _server_messages if present (JSON array of message objects)
        let serverMessages = [];
        try {
          if (typeof imported._server_messages === "string") {
            const parsed = JSON.parse(imported._server_messages);
            if (Array.isArray(parsed)) {
              serverMessages = parsed.map((m) => (typeof m === "string" ? m : m?.message || "")).filter(Boolean);
            }
          }
        } catch (_) {}
        // Show errors in plain, lender-friendly wording (not technical field names)
        const errorDetailLines = backendErrors.map((r) => {
          const friendlyText = formatBackendErrorForLender(r.error);
          return `Row ${r.row}: ${friendlyText}`;
        });
        const maxShow = 20;
        const details = errorDetailLines.length <= maxShow
          ? errorDetailLines
          : [...errorDetailLines.slice(0, maxShow), `... and ${errorDetailLines.length - maxShow} more row(s) with the same or similar issues`];
        if (serverMessages.length > 0) {
          const firstServerMsg = formatBackendErrorForLender(serverMessages[0]);
          details.unshift(firstServerMsg);
        }
        const rowWord = backendErrors.length === 1 ? "row" : "rows";
        setUploadResult({
          type: "error",
          message: `We couldn’t add your products because ${backendErrors.length} ${rowWord} in the file had issues. Please check the details below, update your CSV, and try again.`,
          fileName: uploadedFileName,
          backendErrors: details,
        });
        setUploadStatus("error");
        setShowConfirmation(true);
        setErrors({ upload: "Please fix the issues listed in the message and upload again." });
      } else {
        const result = {
          fileName: uploadedFileName,
          isScheduled: scheduleEnabled && scheduleAt,
          scheduledAt: scheduleEnabled && scheduleAt ? new Date(scheduleAt).toLocaleString() : null,
          totalRows: validationResult.stats.totalRows,
          btlSplit: btlSplitSummary(validationResult.stats),
          uploadInfo: upload,
          importInfo: imported,
        };
        setUploadResult(result);
        setShowConfirmation(true);
        setUploadStatus("success");
      }

      // Reset form after confirmation is closed
    } catch (error) {
      console.error("❌ CSV Import failed:", error);
      setUploadStatus("error");

      const serverMsg =
        error?.data?.message ||
        error?.data?._server_messages ||
        error?.message ||
        "Upload/import failed. Please try again.";

      setErrors({
        upload: serverMsg,
      });

      setShowConfirmation(true);
      setUploadResult({
        type: "error",
        message: serverMsg,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    if (uploadStatus === "success") {
      // Reset form after successful upload
      setFile(null);
      setScheduleEnabled(false);
      setScheduleAt("");
      setUploadStatus(null);
      setValidationResult(null);
      setUploadProgress(0);
      setUploadedFileUrl(null);
      setIsUploadingFile(false);
      // Reset file input element
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="min-h-screen">
      {/* hero */}
      <div className="px-4 md:px-6 mt-2">
        <div className="bg-sky-400 text-white rounded-2xl px-6 py-6">
          <h1 className="text-2xl md:text-3xl font-extrabold">
            Bulk CSV Upload — {productLabel}
          </h1>
          <p className="text-white/90 mt-1">
            Upload all products with a single CSV file
          </p>
        </div>
      </div>

      {/* tabs */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 mt-3">
        <UploadTabs ptype={ptype} />
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-6 mt-6">
        <Card className="p-0">
          <div
            className={`border-2 border-dashed rounded-2xl m-4 md:m-6 transition-colors ${isDragging ? "border-sky-500 bg-sky-50" : "border-sky-200"}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <div className="px-6 pt-12 pb-6 flex flex-col items-stretch space-y-10">
              {/* Validation Status (BTL only) */}
              {validationResult && !isIF && (
                <div className={`mx-auto max-w-2xl w-full rounded-lg p-4 border ${
                  validationResult.valid
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-red-50 border-red-200"
                }`}>
                  <div className="flex items-start gap-3">
                    {validationResult.valid ? (
                      <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        validationResult.valid ? "text-emerald-800" : "text-red-800"
                      }`}>
                        {validationResult.valid
                          ? `Validation passed! ${validationResult.stats.totalRows} row(s) ready to upload.`
                          : `Validation failed: ${validationResult.errors.length} error(s) found`}
                      </p>
                      {validationResult.stats?.totalRows > 0 && validationResult.stats.complexBtlRows != null && (
                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          <span className="px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-800 font-semibold">
                            BTL: {validationResult.stats.btlRows}
                          </span>
                          <span className="px-2 py-0.5 rounded-full border border-violet-200 bg-violet-50 text-violet-800 font-semibold">
                            Complex BTL: {validationResult.stats.complexBtlRows}
                          </span>
                          {(validationResult.stats.complexBreakdown || [])
                            .filter((g) => g.rows > 0)
                            .map((g) => (
                              <span key={g.key} className="px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600">
                                {g.label}: {g.rows}
                              </span>
                            ))}
                        </div>
                      )}
                      {validationResult.warnings.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-amber-700 font-medium mb-1">
                            Warnings ({validationResult.warnings.length}):
                          </p>
                          <ul className="text-xs text-amber-600 list-disc list-inside space-y-0.5">
                            {validationResult.warnings.map((w, idx) => (
                              <li key={idx}>{w}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {!validationResult.valid && validationResult.errors.length > 0 && (
                        <div className="mt-2 max-h-60 overflow-y-auto">
                          <p className="text-xs text-red-700 font-medium mb-1">
                            Errors ({validationResult.errors.length}):
                          </p>
                          <ul className="text-xs text-red-600 list-disc list-inside space-y-0.5">
                            {validationResult.errors.slice(0, 20).map((e, idx) => (
                              <li key={idx}>{e}</li>
                            ))}
                            {validationResult.errors.length > 20 && (
                              <li className="text-red-500 font-medium">
                                ... and {validationResult.errors.length - 20} more error(s)
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Upload widget */}
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-sky-50 border border-sky-100 grid place-items-center">
                  <FileSpreadsheet
                    className="h-12 w-12 text-sky-700"
                    aria-hidden="true"
                  />
                </div>

                <h2 className="mt-4 text-lg font-semibold text-slate-900">
                  Upload Product CSV File
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {isIF
                    ? "Upload an XLSX or CSV file using the Invoice Finance product template"
                    : "Upload a CSV or XLSX of BTL and Complex BTL products using the BTL template"}
                </p>
                <p className="text-xs text-sky-600 mt-0.5">
                  or drag and drop your file here
                </p>

                <div className="mt-5">
                  <label className="inline-flex items-center cursor-pointer">
                    <span className="relative">
                      <button
                        type="button"
                        className="px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-700 disabled:opacity-50"
                        disabled={isUploading}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {isUploading ? "Uploading..." : "Select File"}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={isIF ? ".csv,.xls,.xlsx" : ".csv,.xlsx"}
                        className="absolute inset-0 h-full w-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                        onChange={onPick}
                        disabled={isUploading}
                      />
                    </span>
                  </label>
                </div>

                <div className="mt-2">
                  {file ? (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-slate-600">
                        {file.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {file.size < 1024 * 1024 
                          ? `${(file.size / 1024).toFixed(2)} KB`
                          : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                      </div>
                      {/* Upload Progress Bar - Show when uploading file immediately or during process */}
                      {(isUploadingFile || (isUploading && uploadProgress > 0)) && (
                        <div className="w-full rounded-lg border border-sky-300 bg-sky-50 p-3 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <Loader2 className="h-4 w-4 text-sky-600 animate-spin flex-shrink-0" />
                              <span className="text-xs font-medium text-slate-700 truncate">
                                {isUploadingFile ? "Uploading file..." : "Processing..."}
                              </span>
                            </div>
                            <span className="text-xs font-semibold text-sky-600 ml-2 flex-shrink-0">
                              {uploadProgress}%
                            </span>
                          </div>
                          <div className="w-full bg-sky-200 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-sky-600 h-full rounded-full transition-all duration-300 ease-out"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500">
                      No file selected
                    </div>
                  )}
                </div>
                {errors.file && (
                  <div className="mt-1 text-xs text-red-600">
                    {errors.file}
                  </div>
                )}
              </div>

              {/* Required CSV Format */}
              <div className="max-w-md md:pl-1">
                <div className="text-sm font-semibold text-slate-800">
                  {isIF ? "Required file format:" : "Required CSV / XLSX Format:"}
                </div>
                <ul className="mt-2 text-sm text-slate-600 list-disc list-inside space-y-1">
                  {isIF ? (
                    <>
                      <li>Use the Dynesourcer Invoice Finance XLSX template</li>
                      <li>Sheet name: &quot;Lender Products&quot;</li>
                      <li>Accepted formats: XLSX, XLS, or CSV</li>
                      <li>Maximum file size: 10MB</li>
                    </>
                  ) : (
                    <>
                      <li>Use the BTL template (a title row above the headers is fine)</li>
                      <li>Header row, then sub-header row, then one product per row</li>
                      <li>
                        Complex BTL: fill the coloured columns (HMO, MUFB, MPOT, Holiday / Short Term Lets,
                        Portfolio). Rows without them are uploaded as BTL
                      </li>
                      <li>Expiry date format: dd/mm/yyyy</li>
                      <li>Accepted formats: CSV or XLSX, maximum 10MB</li>
                    </>
                  )}
                </ul>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="mt-3 text-sky-700 hover:text-sky-800 font-medium disabled:opacity-50"
                  disabled={isUploading}
                >
                  {isIF ? "Template info" : "Download CSV Template"}
                </button>
              </div>

              {/* Action Buttons */}
              <div className="pt-2 pb-6 flex flex-col items-center gap-4">
                <div className="flex items-center gap-4">
                  {isIF ? (
                    <button
                      type="button"
                      onClick={processUpload}
                      className="px-5 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-700 disabled:opacity-60 flex items-center gap-2"
                      disabled={!file || isUploading}
                    >
                      {isUploading && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      {isUploading ? "Uploading..." : "Process Upload"}
                    </button>
                  ) : !validationResult?.valid ? (
                    <button
                      type="button"
                      onClick={validateCSV}
                      className="px-5 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-700 disabled:opacity-60 flex items-center gap-2"
                      disabled={!file || isUploading || isValidating}
                    >
                      {isValidating && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      {isValidating ? "Validating..." : "Validate"}
                    </button>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50/50 px-4 py-2">
                        <label className="inline-flex items-center gap-2 text-sky-900 font-semibold text-sm">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={scheduleEnabled}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setScheduleEnabled(checked);
                              setErrors((prev) => ({
                                ...prev,
                                scheduleAt: checked ? prev.scheduleAt : undefined,
                              }));
                            }}
                            disabled={isUploading}
                          />
                          Schedule
                        </label>
                        {scheduleEnabled && (
                          <input
                            type="datetime-local"
                            className={`border rounded p-2 bg-white text-sm ${errors.scheduleAt ? "border-red-500" : ""}`}
                            disabled={isUploading}
                            value={scheduleAt}
                            onChange={(e) => setScheduleAt(e.target.value)}
                          />
                        )}
                      </div>
                      {!scheduleEnabled && (
                        <button
                          type="button"
                          onClick={processUpload}
                          className="px-5 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-700 disabled:opacity-60 flex items-center gap-2"
                          disabled={!file || isUploading}
                        >
                          {isUploading && (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          )}
                          {isUploading ? "Uploading..." : "Process Upload"}
                        </button>
                      )}

                      {scheduleEnabled && (
                        <button
                          type="button"
                          onClick={processUpload}
                          className="px-5 py-2 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-2"
                          disabled={!file || isUploading || !scheduleAt}
                        >
                          {isUploading && (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          )}
                          {isUploading ? "Scheduling..." : "Schedule Upload"}
                        </button>
                      )}
                    </>
                  )}
                </div>
                {errors.scheduleAt && (
                  <p className="text-red-600 text-sm">
                    {errors.scheduleAt}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Confirmation Modal */}
      {uploadResult && (
        <ConfirmationModal
          open={showConfirmation}
          onClose={handleConfirmationClose}
          title={
            uploadResult.type === "error"
              ? "Upload Failed"
              : uploadResult.isScheduled
              ? "Upload Scheduled Successfully"
              : "Upload Successful"
          }
          message={
            uploadResult.type === "error"
              ? uploadResult.message || "Upload failed. Please try again."
              : uploadResult.isScheduled
              ? `Your CSV file "${uploadResult.fileName || "file"}" has been scheduled for upload.`
              : `Your CSV file "${uploadResult.fileName || "file"}" has been uploaded and processed successfully.`
          }
          type={uploadResult.type === "error" ? "error" : "success"}
          details={
            uploadResult.type === "error"
              ? (uploadResult.backendErrors || null)
              : uploadResult.ifSummary
              ? [
                  `File: ${uploadResult.fileName || "N/A"}`,
                  `Total rows: ${uploadResult.totalRows ?? 0}`,
                  `Created: ${uploadResult.ifSummary.created}`,
                  `Updated: ${uploadResult.ifSummary.updated}`,
                  uploadResult.ifSummary.skipped
                    ? `Skipped: ${uploadResult.ifSummary.skipped}`
                    : null,
                  uploadResult.ifSummary.warningCount
                    ? `Warnings: ${uploadResult.ifSummary.warningCount} (rows imported with minor issues)`
                    : "Products are now live in the system",
                ].filter(Boolean)
              : [
                  `File: ${uploadResult.fileName || "N/A"}`,
                  `Total rows processed: ${uploadResult.totalRows || 0}`,
                  uploadResult.btlSplit ? `Product split: ${uploadResult.btlSplit}` : null,
                  uploadResult.isScheduled
                    ? `Scheduled for: ${uploadResult.scheduledAt || "N/A"}`
                    : "Products are now live in the system",
                ].filter(Boolean)
          }
          confirmLabel="OK"
          showCancel={false}
        />
      )}
    </div>
  );
}

